import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar as MuiAvatar,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
} from '@mui/material';
import { MapPin, Eye, MoreVertical, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatusBadge } from '@/components/common';

interface CandidateTableProps {
  candidates: any[];
  onViewDetail: (candidate: any) => void;
  onActionClick?: (e: React.MouseEvent<HTMLButtonElement>, candidateId: string) => void;
  onUnsave?: (candidate: any) => void;
  pageSize?: number;
  setPageSize?: (size: number) => void;
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  totalPages?: number;
}

// Reusable status badge renderer using shared StatusBadge component
export const renderStatusBadge = (status: string, label: string) => {
  return <StatusBadge status={status} label={label} />;
};

export const renderStageBadge = (stage: string) => {
  const maps: Record<string, { bg: string; text: string; label: string }> = {
    NEW: { bg: 'bg-[#fff7ed]', text: 'text-[#ea580c]', label: 'Mới ứng tuyển' },
    CONTACTED: { bg: 'bg-[#eef2ff]', text: 'text-[#6366f1]', label: 'Liên hệ' },
    INTERVIEW: { bg: 'bg-[#f5f3ff]', text: 'text-[#8b5cf6]', label: 'Phỏng vấn' },
    PROBATION: { bg: 'bg-[#fdf2f8]', text: 'text-[#ec4899]', label: 'Thử việc' },
    HIRED: { bg: 'bg-[#ecfdf5]', text: 'text-[#10b981]', label: 'Tuyển dụng (Hired)' },
    REJECTED: { bg: 'bg-[#fef2f2]', text: 'text-[#ef4444]', label: 'Từ chối' }
  };
  const style = maps[stage] || maps.NEW;
  return (
    <span className={`px-3 py-1.5 rounded-xl font-bold text-[11px] tracking-wide whitespace-nowrap ${style.bg} ${style.text}`}>
      {style.label}
    </span>
  );
};

export function CandidateTable({
  candidates,
  onViewDetail,
  onActionClick,
  onUnsave,
  pageSize = 10,
  setPageSize,
  currentPage = 1,
  setCurrentPage,
  totalPages = 1
}: CandidateTableProps) {

  return (
    <div className="flex flex-col h-full w-full">
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: '24px',
          overflowX: 'auto',
          boxShadow: 'none',
          border: 'none',
          '&::-webkit-scrollbar': {
            height: '6px',
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f8fafc',
            borderRadius: '999px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#cbd5e1',
            borderRadius: '999px',
            '&:hover': {
              backgroundColor: '#94a3b8',
            },
          },
        }}
      >
        <Table sx={{ minWidth: 1050 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #f1f3f5' }}>
              <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Ứng viên</TableCell>
              <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Vị trí & Hình thức</TableCell>
              <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Kinh nghiệm / Học vấn</TableCell>
              <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Mức lương</TableCell>
              <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Khu vực</TableCell>
              <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Quy trình</TableCell>
              <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Trạng thái</TableCell>
              <TableCell sx={{ py: 2.5, px: 3, fontWeight: '700', color: '#64748b', fontSize: '11px', textTransform: 'uppercase', tracking: '0.05em', whiteSpace: 'nowrap' }}>Ghi chú</TableCell>
              <TableCell
                sx={{
                  py: 2.5,
                  px: 3,
                  fontWeight: '700',
                  color: '#64748b',
                  fontSize: '11px',
                  textTransform: 'uppercase',
                  tracking: '0.05em',
                  whiteSpace: 'nowrap',
                  textAlign: 'center',
                  position: 'sticky',
                  right: 0,
                  backgroundColor: '#f8fafc',
                  borderBottom: '1px solid #f1f3f5',
                  zIndex: 2,
                  boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.05)',
                }}
              >
                Thao tác
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {candidates.map((candidate) => (
              <TableRow
                key={candidate.id}
                hover
                onDoubleClick={() => onViewDetail(candidate)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#fafafc !important' }
                }}
              >
                {/* Candidate Info Profile */}
                <TableCell sx={{ py: 2, px: 3 }}>
                  <div className="flex items-center gap-3">
                    {candidate.avatar ? (
                      <MuiAvatar src={candidate.avatar} alt={candidate.fullName} sx={{ width: 44, height: 44, border: '1px solid #ffe8df' }} />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 font-bold text-lg uppercase shrink-0 border border-orange-200">
                        {candidate.fullName?.charAt(0) || 'U'}
                      </div>
                    )}
                    <div>
                      <h4 className="font-bold text-gray-800 leading-snug hover:text-[#f97316] transition-colors cursor-pointer line-clamp-1" onClick={() => onViewDetail(candidate)}>
                        {candidate.fullName}
                      </h4>
                      <span className="text-[11px] text-gray-400 font-semibold block mt-0.5">{candidate.email}</span>
                      {candidate.phoneNumber && (
                        <span className="text-[11px] text-gray-400 font-semibold block">{candidate.phoneNumber}</span>
                      )}
                    </div>
                  </div>
                </TableCell>

                {/* Position & Type */}
                <TableCell sx={{ py: 2, px: 3 }}>
                  <div className="text-sm font-bold text-gray-700 line-clamp-1">{candidate.position}</div>
                  <div className="text-xs text-gray-500 mt-0.5 font-medium line-clamp-1">{candidate.categoryAndType}</div>
                </TableCell>

                {/* Experience & Education */}
                <TableCell sx={{ py: 2, px: 3 }}>
                  <div className="text-sm font-bold text-gray-700">{candidate.experience}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{candidate.education}</div>
                </TableCell>

                {/* Expected Salary */}
                <TableCell sx={{ py: 2, px: 3, fontWeight: 'bold', color: '#334155', fontSize: '13px' }}>
                  {candidate.salary}
                </TableCell>

                {/* Location */}
                <TableCell sx={{ py: 2, px: 3 }}>
                  <div className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-600">
                    <MapPin size={14} className="text-blue-400 shrink-0" />
                    <span className="line-clamp-1">{candidate.location}</span>
                  </div>
                </TableCell>

                {/* Stage Badge (Quy trình) */}
                <TableCell sx={{ py: 2, px: 3 }}>
                  {renderStageBadge(candidate.stage || 'NEW')}
                </TableCell>

                {/* Styled Status Badge */}
                <TableCell sx={{ py: 2, px: 3 }}>
                  {renderStatusBadge(candidate.status || 'pending', candidate.statusLabel || 'Đang chờ duyệt')}
                </TableCell>

                {/* Recruiter Notes */}
                <TableCell sx={{ py: 2, px: 3 }}>
                  <div className="text-xs text-gray-500 italic max-w-[150px] line-clamp-2" title={candidate.recruiterNote}>
                    {candidate.recruiterNote || ''}
                  </div>
                </TableCell>

                {/* Actions Column (MUI Ripple buttons with Tooltips) */}
                <TableCell
                  sx={{
                    py: 2,
                    px: 3,
                    textAlign: 'center',
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#ffffff',
                    zIndex: 1,
                    boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.05)',
                  }}
                >
                  <div className="flex items-center justify-center gap-1.5">

                    <Tooltip title="Xem chi tiết hồ sơ" arrow>
                      <IconButton
                        onClick={() => onViewDetail(candidate)}
                        size="small"
                        sx={{
                          border: '1px solid #f1f3f5',
                          borderRadius: '12px',
                          '&:hover': { backgroundColor: '#fff2eb', color: '#f97316', borderColor: '#ffe2d4' }
                        }}
                      >
                        <Eye size={15} />
                      </IconButton>
                    </Tooltip>

                    {onUnsave ? (
                      <Tooltip title="Bỏ lưu ứng viên" arrow>
                        <IconButton
                          onClick={() => onUnsave(candidate)}
                          size="small"
                          sx={{
                            border: '1px solid #fee2e2',
                            borderRadius: '12px',
                            color: '#ef4444',
                            backgroundColor: '#fef2f2',
                            '&:hover': { backgroundColor: '#fee2e2' }
                          }}
                        >
                          <Heart size={15} className="fill-[#ef4444]" />
                        </IconButton>
                      </Tooltip>
                    ) : onActionClick ? (
                      <Tooltip title="Thao tác nhanh" arrow>
                        <IconButton
                          onClick={(e) => onActionClick(e, candidate.id)}
                          size="small"
                          sx={{
                            border: '1px solid #f1f3f5',
                            borderRadius: '12px',
                            '&:hover': { backgroundColor: '#f1f3f5' }
                          }}
                        >
                          <MoreVertical size={15} />
                        </IconButton>
                      </Tooltip>
                    ) : null}

                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Table Footer Controls */}
      {setPageSize && setCurrentPage && (
        <div className="bg-white border-t border-gray-100 p-5 flex flex-col sm:flex-row items-center justify-between gap-4 mt-auto rounded-b-3xl">
          {/* Left: Row per page count selector */}
          <div className="flex items-center gap-2.5 text-xs text-gray-400 font-bold">
            <span>Hiển thị</span>
            <Select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              size="small"
              sx={{
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 'bold',
                color: '#374151',
                bgcolor: '#f9fafb',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e5e7eb',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f97316',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#f97316',
                },
                '& .MuiSelect-select': {
                  py: 1,
                  px: 2,
                }
              }}
            >
              <MenuItem value={5} sx={{ fontSize: '13px' }}>5</MenuItem>
              <MenuItem value={10} sx={{ fontSize: '13px' }}>10</MenuItem>
              <MenuItem value={12} sx={{ fontSize: '13px' }}>12</MenuItem>
              <MenuItem value={20} sx={{ fontSize: '13px' }}>20</MenuItem>
              <MenuItem value={50} sx={{ fontSize: '13px' }}>50</MenuItem>
            </Select>
            <span>dòng</span>
          </div>

          {/* Right: Realistic Pagination buttons and descriptions */}
          <div className="flex flex-col sm:items-end gap-2">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 rounded-xl border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <ChevronLeft size={16} />
              </button>

              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={cn(
                    "w-9 h-9 rounded-xl text-xs font-bold transition-all shadow-sm",
                    currentPage === page
                      ? "bg-gradient-to-br from-[#f97316] to-[#ff7e2e] text-white border-none"
                      : "bg-white border border-gray-100 text-gray-500 hover:bg-gray-50"
                  )}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 rounded-xl border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-transparent"
              >
                <ChevronRight size={16} />
              </button>
            </div>
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider text-right">
              Đang xem trang {currentPage} / {totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
